import { useEffect, useState, type FormEvent } from "react";
import { AiClientError, sendAiChat } from "../../api/ai-client";
import type { AiChatMessage, AiChatResponse } from "../../api/ai.types";
import { getOrCreateAuthSession } from "../../api/auth-client";
import type { AuthSessionResponse } from "../../api/auth.types";
import {
  createConversation,
  getConversationMessages,
  listConversations,
  saveConversationMessage
} from "../../api/conversation-client";
import type { OmnisyniaClientModule } from "../module.types";
import { getActiveUltraOrganizationId } from "../../api/ultra-client";

function ChatOmnisynia() {
  const greeting: AiChatMessage = {
    role: "assistant",
    content: "Hola, soy OMNISYNIA. El motor IA está conectado. Pregúntame lo que necesites."
  };
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<AiChatMessage[]>([greeting]);
  const [session, setSession] = useState<AuthSessionResponse | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [lastResponse, setLastResponse] = useState<AiChatResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const activeSession = await getOrCreateAuthSession();
        let conversation = (await listConversations(activeSession.sessionToken))[0];
        if (!conversation) conversation = await createConversation(activeSession.sessionToken);
        const history = await getConversationMessages(activeSession.sessionToken, conversation.id);
        setSession(activeSession);
        setConversationId(conversation.id);
        setMessages(history.length ? history.map(({ role, content }) => ({ role, content })) : [greeting]);
      } catch (err) {
        setError(err instanceof Error ? err.message : "No se pudo cargar el historial.");
      } finally {
        setIsInitializing(false);
      }
    })();
  }, []);

  async function startNewConversation() {
    if (!session || isLoading) return;
    const conversation = await createConversation(session.sessionToken);
    setConversationId(conversation.id);
    setMessages([greeting]);
    setLastResponse(null);
  }

  async function sendMessage(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const cleanInput = input.trim();

    if (!cleanInput || isLoading || !session || !conversationId) {
      return;
    }

    const nextMessages: AiChatMessage[] = [
      ...messages,
      {
        role: "user",
        content: cleanInput
      }
    ];

    setMessages(nextMessages);
    setInput("");
    setIsLoading(true);
    setError(null);

    try {
      await saveConversationMessage(session.sessionToken, conversationId, {
        role: "user",
        content: cleanInput
      });
      const response = await sendAiChat({
        moduleId: "chat-omnisynia",
        sessionId: session.sessionId,
        sessionToken: session.sessionToken,
        organizationId: getActiveUltraOrganizationId() ?? undefined,
        messages: nextMessages,
        metadata: {
          locale: "es",
          platform: "web"
        },
        userConsent: {
          privacyPolicyAccepted: true,
          termsAccepted: true,
          aiDisclosureAccepted: true,
          safetyPolicyAccepted: true,
          memoryOptIn: false
        }
      });

      await saveConversationMessage(session.sessionToken, conversationId, response.message);

      setLastResponse(response);
      setMessages([
        ...nextMessages,
        {
          role: "assistant",
          content: response.message.content
        }
      ]);
    } catch (err) {
      setError(
        err instanceof AiClientError
          ? `${err.message} (${err.code})`
          : "No se pudo conectar con el motor IA."
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <section className="editor-shell" aria-labelledby="chat-omnisynia-title">
      <header className="editor-header">
        <div>
          <p className="eyebrow">CHAT PRINCIPAL · OMNISYNIA CORE</p>
          <h2 id="chat-omnisynia-title">Chat OMNISYNIA</h2>
          <p>
            Conversación general conectada al backend, OpenAI y la base Postgres real.
          </p>
        </div>
        <span className="status-pill">AI READY</span>
      </header>

      <div className="ai-panel">
        <div className="ai-panel-header">
          <div>
            <p className="eyebrow">MOTOR IA CONECTADO</p>
            <h3>Habla con OMNISYNIA</h3>
          </div>
          <span className="status-pill">POSTGRES READY</span>
        </div>

        <button
          className="secondary-button"
          disabled={!session || isLoading}
          onClick={() => void startNewConversation()}
          type="button"
        >
          Nueva conversación
        </button>

        <div className="ai-response" aria-label="Conversación OMNISYNIA">
          {messages.map((message, index) => (
            <p key={`${message.role}-${index}`}>
              <strong>{message.role === "user" ? "Tú" : "OMNISYNIA"}:</strong>{" "}
              {message.content}
            </p>
          ))}
        </div>

        <form onSubmit={sendMessage}>
          <label>
            Escribe tu mensaje
            <textarea
              value={input}
              onChange={(event) => setInput(event.target.value)}
              maxLength={2000}
              rows={5}
              placeholder="Escribe aquí lo que quieres preguntarle a OMNISYNIA..."
            />
          </label>

          <button className="primary-button" disabled={isInitializing || isLoading || !input.trim()} type="submit">
            {isInitializing ? "Cargando historial..." : isLoading ? "Pensando..." : "Enviar a OMNISYNIA"}
          </button>
        </form>

        {error ? <p className="error-message">{error}</p> : null}

        {lastResponse ? (
          <article className="ai-response" aria-label="Estado técnico del motor IA">
            <header>
              <span>{lastResponse.provider}</span>
              <span>{lastResponse.model}</span>
              <span>{lastResponse.route.moduleId}</span>
              <span>Sesión: {lastResponse.session.userMode}</span>
            </header>
          </article>
        ) : null}
      </div>
    </section>
  );
}

export const chatOmnisyniaClientModule: OmnisyniaClientModule = {
  id: "chat-omnisynia",
  displayName: "Chat OMNISYNIA",
  description: "Chat principal general de OMNISYNIA One Intelligence.",
  tier: "core",
  status: "available",
  Component: ChatOmnisynia
};
